import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';
import type { FinancePeriod } from './use-finance-summary.js';

export interface TerminalFacility {
  facility: string;
  stops: number;
  drivers: number;
}

export interface TerminalPnlEntry {
  isp_number: string;
  terminal?: string;
  name: string;
  total_revenue: number;
  total_charges: number;
  chargebacks: number;
  net_settlement: number;
  total_stops: number;
  driver_count: number;
  dollar_per_stop: number;
  driver_payroll: number | null;
  net_income: number | null;
  revenue_by_type: Record<string, number>;
  charges_by_type: Record<string, number>;
  facilities: TerminalFacility[];
}

export interface TerminalPnlData {
  period: FinancePeriod;
  terminals: TerminalPnlEntry[];
  company_avg_dollar_per_stop: number;
  radar_data: Record<string, string | number>[];
}

interface TerminalPnlResponse extends TerminalPnlData {
  error?: string;
}

export function useFinanceTerminalPnl(periodType: string) {
  const [data, setData] = useState<TerminalPnlData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (periodType) params.set('period', periodType);
      const resp = await apiFetch<TerminalPnlResponse>(`/api/finance/terminal-pnl?${params.toString()}`);
      if (resp?.error) throw new Error(resp.error);
      setData(resp);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Terminal P&L load failed');
    } finally {
      setLoading(false);
    }
  }, [periodType]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, refresh: load };
}
